import {
  Box,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useTranslation } from "react-i18next";
import { evaluatePassword } from "../../utils/passwordPolicy";

const PasswordStrengthChecklist = ({ password, username, email }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const policy = evaluatePassword(password, { username, email });
  const total = policy.checks.length;
  const progress = Math.round((policy.score / total) * 100);
  const touched = password.length > 0;
  const strengthKey =
    policy.isValid || policy.score >= total - 1
      ? "strong"
      : policy.score >= Math.ceil(total / 2)
        ? "almost"
        : "weak";

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 1.5,
        bgcolor: "rgba(255, 250, 240, 0.72)",
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="body2" fontWeight={700}>
            {t("authRegister.passwordPolicy.title")}
          </Typography>
          <Typography
            variant="caption"
            fontWeight={800}
            color={policy.isValid ? "success.main" : "text.secondary"}
            sx={{ textTransform: "uppercase", letterSpacing: 1 }}
          >
            {t(`authRegister.passwordPolicy.${strengthKey}`)}
          </Typography>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={touched ? progress : 0}
          color={policy.isValid ? "success" : "warning"}
          sx={{
            height: 7,
            borderRadius: 999,
            bgcolor: "rgba(16, 48, 43, 0.12)",
          }}
        />

        <Stack spacing={0.5}>
          {policy.checks.map((check) => {
            const Icon = check.valid ? CheckCircleIcon : RadioButtonUncheckedIcon;

            return (
              <Stack
                key={check.id}
                direction="row"
                alignItems="center"
                spacing={0.75}
              >
                <Icon
                  fontSize="small"
                  color={check.valid ? "success" : "disabled"}
                />
                <Typography
                  variant="caption"
                  color={check.valid ? "text.primary" : "text.secondary"}
                >
                  {t(`authRegister.passwordPolicy.checks.${check.id}`)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PasswordStrengthChecklist;
